import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import GlassyModal from 'components/ui/GlassyModal';
import Icon from 'components/AppIcon';
import { useAuth } from 'contexts/AuthContext';
import { useProfiles } from 'hooks/useProfiles';
import { db } from '../../firebaseClient';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { toast } from 'sonner';

const AdminClassCreationForm = ({ isOpen, onClose, onSubmit }) => {
  const { user, profile } = useAuth();
  const { profiles } = useProfiles();
  const [loading, setLoading] = useState(false);
  const { register, handleSubmit, formState: { errors }, reset } = useForm({
    defaultValues: {
      client_id: '',
      class_title: '',
      subject: '',
      class_forum: '',
      start_date: '',
      end_date: '',
      preferred_communication: '',
      assigned_writer: '',
      status: 'pending',
      requirements: '',
      notes: ''
    }
  });

  // Get client profiles (users with is_client: true)
  const clientProfiles = profiles.filter(p => p.is_client === true);
  
  // Get writer profiles
  const writerProfiles = profiles.filter(p => p.is_writer === true);

  const subjects = [
    'Mathematics', 'Computer Science', 'Physics', 'Chemistry', 'Engineering', 'Statistics',
    'Business Administration', 'Economics', 'Marketing', 'Finance', 'Management', 'Accounting',
    'English Literature', 'History', 'Psychology', 'Sociology', 'Philosophy', 'Political Science',
    'Biology', 'Medicine', 'Nursing', 'Public Health', 'Pharmacy', 'Health Sciences',
    'Art', 'Design', 'Architecture', 'Music', 'Film Studies', 'Creative Writing',
    'Education', 'Special Education', 'Curriculum Development', 'Educational Technology',
    'Law', 'Criminal Justice', 'Legal Studies', 'Paralegal Studies',
    'Environmental Science', 'Geography', 'Geology', 'Meteorology',
    'Anthropology', 'Archaeology', 'Linguistics', 'International Relations',
    'Journalism', 'Communications', 'Public Relations', 'Media Studies',
    'Other'
  ];

  const classForumOptions = [
    'Canvas', 'Blackboard', 'Moodle', 'Google Classroom', 'Microsoft Teams', 
    'Schoology', 'Edmodo', 'Zoom', 'Discord', 'Other'
  ];

  const communicationOptions = [
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
    { value: 'discord', label: 'Discord' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'other', label: 'Other' }
  ];

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'under-review', label: 'Under Review' },
    { value: 'revision-needed', label: 'Revision Needed' }
  ];

  const handleFormSubmit = async (data) => {
    setLoading(true);
    
    try {
      // Find the selected client
      const selectedClient = clientProfiles.find(c => c.id === data.client_id);
      
      if (!selectedClient) {
        toast.error('Please select a valid client');
        return;
      }

      // Create class document
      const classData = {
        user_id: selectedClient.uid,
        email: selectedClient.email,
        name: selectedClient.full_name || selectedClient.email,
        class_title: data.class_title,
        subject: data.subject,
        class_forum: data.class_forum,
        start_date: data.start_date ? new Date(data.start_date) : null,
        end_date: data.end_date ? new Date(data.end_date) : null,
        preferred_communication: data.preferred_communication,
        assigned_writer: data.assigned_writer || null,
        status: data.status,
        requirements: data.requirements,
        notes: data.notes,
        created_by: user.uid,
        created_by_name: profile?.full_name || user.email,
        created_at: serverTimestamp()
      };

      await addDoc(collection(db, 'class_management'), classData);
      
      toast.success('Class created successfully for ' + selectedClient.full_name);
      reset();
      onSubmit?.();
      onClose();
    } catch (error) {
      console.error('Error creating class:', error);
      toast.error('Failed to create class: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <GlassyModal
      isOpen={isOpen}
      onClose={() => {
        onClose();
        reset();
      }}
      title="Create Class for Client"
      icon="BookOpen"
      maxWidth="max-w-4xl"
    >
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
        
        {/* Client Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Select Client *
            </label>
            <select
              {...register('client_id', { required: 'Please select a client' })}
              className="w-full px-4 py-2 bg-white/20 border border-white/30 rounded-lg text-white focus:outline-none focus:border-primary"
            >
              <option value="">Choose a client...</option>
              {clientProfiles.map(client => (
                <option key={client.id} value={client.id} className="bg-gray-800">
                  {client.full_name || client.email}
                </option>
              ))}
            </select>
            {errors.client_id && (
              <p className="text-red-400 text-sm mt-1">{errors.client_id.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Class Title *
            </label>
            <input
              type="text"
              {...register('class_title', { required: 'Class title is required' })}
              className="w-full px-4 py-2 bg-white/20 border border-white/30 rounded-lg text-white focus:outline-none focus:border-primary"
              placeholder="Enter class title"
            />
            {errors.class_title && (
              <p className="text-red-400 text-sm mt-1">{errors.class_title.message}</p>
            )}
          </div>
        </div>

        {/* Subject and Forum */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Subject *
            </label>
            <select
              {...register('subject', { required: 'Subject is required' })}
              className="w-full px-4 py-2 bg-white/20 border border-white/30 rounded-lg text-white focus:outline-none focus:border-primary"
            >
              <option value="">Select subject...</option>
              {subjects.map(subject => (
                <option key={subject} value={subject} className="bg-gray-800">
                  {subject}
                </option>
              ))}
            </select>
            {errors.subject && (
              <p className="text-red-400 text-sm mt-1">{errors.subject.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Class Forum *
            </label>
            <select
              {...register('class_forum', { required: 'Class forum is required' })}
              className="w-full px-4 py-2 bg-white/20 border border-white/30 rounded-lg text-white focus:outline-none focus:border-primary"
            >
              <option value="">Select forum...</option>
              {classForumOptions.map(forum => (
                <option key={forum} value={forum} className="bg-gray-800">
                  {forum}
                </option>
              ))}
            </select>
            {errors.class_forum && (
              <p className="text-red-400 text-sm mt-1">{errors.class_forum.message}</p>
            )}
          </div>
        </div>

        {/* Start and End Dates */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Start Date
            </label>
            <input
              type="date"
              {...register('start_date')}
              className="w-full px-4 py-2 bg-white/20 border border-white/30 rounded-lg text-white focus:outline-none focus:border-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-2">
              End Date
            </label>
            <input
              type="date"
              {...register('end_date')}
              className="w-full px-4 py-2 bg-white/20 border border-white/30 rounded-lg text-white focus:outline-none focus:border-primary"
            />
          </div>
        </div>

        {/* Requirements */}
        <div>
          <label className="block text-sm font-medium text-white mb-2">
            Requirements
          </label>
          <textarea
            {...register('requirements')}
            rows={4}
            className="w-full px-4 py-2 bg-white/20 border border-white/30 rounded-lg text-white focus:outline-none focus:border-primary resize-none"
            placeholder="Enter class requirements and instructions..."
          />
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-white mb-2">
            Notes
          </label>
          <textarea
            {...register('notes')}
            rows={3}
            className="w-full px-4 py-2 bg-white/20 border border-white/30 rounded-lg text-white focus:outline-none focus:border-primary resize-none"
            placeholder="Any additional notes or comments..."
          />
        </div>

        {/* Class Details */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Preferred Communication
            </label>
            <select
              {...register('preferred_communication')}
              className="w-full px-4 py-2 bg-white/20 border border-white/30 rounded-lg text-white focus:outline-none focus:border-primary"
            >
              <option value="">Select method...</option>
              {communicationOptions.map(option => (
                <option key={option.value} value={option.value} className="bg-gray-800">
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Assign Writer
            </label>
            <select
              {...register('assigned_writer')}
              className="w-full px-4 py-2 bg-white/20 border border-white/30 rounded-lg text-white focus:outline-none focus:border-primary"
            >
              <option value="">No writer assigned</option>
              {writerProfiles.map(writer => (
                <option key={writer.id} value={writer.id} className="bg-gray-800">
                  {writer.full_name || writer.email}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Initial Status
            </label>
            <select
              {...register('status')}
              className="w-full px-4 py-2 bg-white/20 border border-white/30 rounded-lg text-white focus:outline-none focus:border-primary"
            >
              {statusOptions.map(option => (
                <option key={option.value} value={option.value} className="bg-gray-800">
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end space-x-4 pt-4">
          <button
            type="button"
            onClick={() => {
              onClose();
              reset();
            }}
            className="px-6 py-2 border border-white/30 text-white rounded-lg hover:bg-white/10 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-primary hover:bg-primary/80 text-white rounded-lg font-medium transition-colors flex items-center space-x-2 disabled:opacity-50"
          >
            {loading ? (
              <>
                <Icon name="Loader2" className="animate-spin" size={16} />
                <span>Creating...</span>
              </>
            ) : (
              <>
                <Icon name="Plus" size={16} />
                <span>Create Class</span>
              </>
            )}
          </button>
        </div>
      </form>
    </GlassyModal>
  );
};

export default AdminClassCreationForm;